/**
 * =============================================================================
 * SPEQ55 RFP Pro - Protected Route Component
 * =============================================================================
 *
 * This component provides route protection for authenticated-only pages.
 * It wraps routes that require authentication and/or specific roles.
 *
 * FUNCTIONALITY:
 * --------------
 * 1. Checks if user is authenticated
 * 2. Optionally checks if user has required role(s)
 * 3. Shows loading state while checking authentication
 * 4. Redirects to login if not authenticated
 * 5. Shows unauthorized message if authenticated but wrong role
 * 6. Renders children if all checks pass
 *
 * HOW IT WORKS:
 * -------------
 * 1. Component mounts and checks AuthContext
 * 2. If still loading, show loading spinner
 * 3. If not authenticated, redirect to /login with return URL
 * 4. If authenticated but wrong role, show unauthorized page
 * 5. If all checks pass, render the protected content
 *
 * USAGE:
 * ------
 * // Basic protection (any authenticated user)
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * // Role-based protection (specific roles)
 * <ProtectedRoute requiredRoles={['super_admin', 'admin_sales']}>
 *   <AdminPanel />
 * </ProtectedRoute>
 *
 * // Minimum role level protection
 * <ProtectedRoute minRole="org_admin">
 *   <OrgSettings />
 * </ProtectedRoute>
 *
 * IN REACT ROUTER:
 * ----------------
 * <Route
 *   path="/dashboard"
 *   element={
 *     <ProtectedRoute>
 *       <Dashboard />
 *     </ProtectedRoute>
 *   }
 * />
 *
 * SECURITY CONSIDERATIONS:
 * ------------------------
 * - This is CLIENT-SIDE protection only
 * - Backend must also verify authentication on API calls
 * - Never trust client-side auth checks for sensitive operations
 * - This provides UX improvement, not security
 *
 * @module components/auth/ProtectedRoute
 * @author SPEQ55
 */

import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// =============================================================================
// LOADING COMPONENT
// =============================================================================

/**
 * Loading spinner component shown while checking authentication.
 *
 * This provides visual feedback during the initial auth check,
 * preventing a flash of login page for authenticated users.
 *
 * @returns {JSX.Element} Loading spinner
 */
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        {/* Spinning circle animation */}
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        <p className="mt-4 text-gray-600">Checking authentication...</p>
      </div>
    </div>
  );
}

// =============================================================================
// UNAUTHORIZED COMPONENT
// =============================================================================

/**
 * Unauthorized access component.
 *
 * Shown when user is authenticated but doesn't have the required role.
 * Provides a clear message and option to go back.
 *
 * @param {Object} props - Component props
 * @param {string} props.userRole - User's current role
 * @param {string[]|string} props.requiredRoles - Required role(s) for access
 * @returns {JSX.Element} Unauthorized message
 */
function Unauthorized({ userRole, requiredRoles: _requiredRoles }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
          <svg
            className="h-8 w-8 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>

        {/* Message */}
        <p className="text-gray-600 mb-6">
          You don't have permission to access this page.
          {userRole && (
            <span className="block mt-2 text-sm">
              Your role: <span className="font-medium">{userRole}</span>
            </span>
          )}
        </p>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={() => window.history.back()}
            className="w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Go Back
          </button>
          <a
            href="/"
            className="block w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    </div>
  );
}

Unauthorized.propTypes = {
  userRole: PropTypes.string,
  requiredRoles: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

// =============================================================================
// PROTECTED ROUTE COMPONENT
// =============================================================================

/**
 * Protected Route wrapper component.
 *
 * Wraps route content with authentication and authorization checks.
 * Redirects unauthenticated users to login and shows unauthorized
 * message for users without required roles.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Protected content to render
 * @param {string[]} [props.requiredRoles] - Specific roles allowed to access
 * @param {string} [props.minRole] - Minimum role level required
 * @param {string} [props.redirectTo] - Custom redirect path for unauthenticated users
 * @returns {JSX.Element} Protected content or redirect/unauthorized
 *
 * @example
 * // Any authenticated user
 * <ProtectedRoute>
 *   <ProfilePage />
 * </ProtectedRoute>
 *
 * @example
 * // Specific roles only
 * <ProtectedRoute requiredRoles={['super_admin']}>
 *   <SuperAdminPanel />
 * </ProtectedRoute>
 *
 * @example
 * // Minimum role level
 * <ProtectedRoute minRole="admin_sales">
 *   <ReportsPage />
 * </ProtectedRoute>
 */
function ProtectedRoute({ children, requiredRoles = null, minRole = null, redirectTo = '/login' }) {
  // ===========================================================================
  // HOOKS
  // ===========================================================================

  // Get auth state and helper functions
  const { isAuthenticated, isLoading, user, hasRole, hasMinRole } = useAuth();

  // Get current location for redirect back after login
  const location = useLocation();

  // ===========================================================================
  // AUTHORIZATION CHECKS
  // ===========================================================================

  /**
   * Checks if user has the required role(s).
   *
   * @returns {boolean} True if user passes role check
   */
  const checkRoleAuthorization = () => {
    // If no role requirements, any authenticated user is allowed
    if (!requiredRoles && !minRole) {
      return true;
    }

    // Check specific roles
    if (requiredRoles) {
      // Convert single role to array for consistent handling
      const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
      return hasRole(roles);
    }

    // Check minimum role level
    if (minRole) {
      return hasMinRole(minRole);
    }

    return true;
  };

  // ===========================================================================
  // RENDER LOGIC
  // ===========================================================================

  // Step 1: Show loading while checking authentication
  // This prevents flash of login page for authenticated users
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Step 2: Redirect to login if not authenticated
  // Pass the current location so we can redirect back after login
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Step 3: Check role authorization
  if (!checkRoleAuthorization()) {
    return <Unauthorized userRole={user?.role} requiredRoles={requiredRoles || minRole} />;
  }

  // Step 4: All checks passed - render the protected content
  return children;
}

// =============================================================================
// PROP TYPES
// =============================================================================

ProtectedRoute.propTypes = {
  /**
   * The protected content to render when all checks pass.
   */
  children: PropTypes.node.isRequired,

  /**
   * Array of role names that are allowed to access this route.
   * If provided, user must have one of these roles.
   *
   * @example
   * requiredRoles={['super_admin', 'admin_sales']}
   */
  requiredRoles: PropTypes.arrayOf(PropTypes.string),

  /**
   * Minimum role level required to access this route.
   * User must have this role or higher in the hierarchy.
   * Role hierarchy: end_user < org_admin < admin_sales < super_admin
   *
   * @example
   * minRole="org_admin"
   */
  minRole: PropTypes.oneOf(['end_user', 'org_admin', 'admin_sales', 'super_admin']),

  /**
   * Custom path to redirect to when user is not authenticated.
   * Defaults to '/login'.
   *
   * @example
   * redirectTo="/auth/login"
   */
  redirectTo: PropTypes.string,
};

// =============================================================================
// ADDITIONAL HELPER COMPONENTS
// =============================================================================

/**
 * AdminRoute - Shorthand for routes requiring admin access.
 * Requires admin_sales or super_admin role.
 */
export function AdminRoute({ children }) {
  return <ProtectedRoute requiredRoles={['super_admin', 'admin_sales']}>{children}</ProtectedRoute>;
}

AdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * SuperAdminRoute - Shorthand for routes requiring super admin access.
 * Only super_admin role can access.
 */
export function SuperAdminRoute({ children }) {
  return <ProtectedRoute requiredRoles={['super_admin']}>{children}</ProtectedRoute>;
}

SuperAdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * OrgAdminRoute - Shorthand for routes requiring org admin or higher.
 * Requires org_admin, admin_sales, or super_admin role.
 */
export function OrgAdminRoute({ children }) {
  return <ProtectedRoute minRole="org_admin">{children}</ProtectedRoute>;
}

OrgAdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default ProtectedRoute;
